import {
  Archive,
  Download,
  FilterX,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Plus,
  RotateCcw,
  Search,
  Table as TableIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangeFilter, endOfDayParam } from '@/components/ui/date-range-filter';
import { MemberSelect } from '@/features/members';
import { CRM_OWNER_ROLES } from '@/features/crm/constants';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import { exportCsv, type ApiError } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatMoney } from '@/lib/money';
import { sumBase } from '@/lib/money-totals';
import { BASE as PROPOSALS_BASE } from '../api';
import { PROPOSAL_STATUSES, statusLabel, statusVariant } from '../constants';
import type { Proposal } from '../types';
import { useArchiveProposal, useProposals, usePurgeProposal, useRestoreProposal } from '../queries';
import { useProposalsUi, type ProposalsViewMode } from '../store/viewPrefs';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type ProposalOrderingColumn = 'title' | 'status' | 'base_total' | 'created' | 'modified';

const VIEW_MODES: readonly ViewModeOption<ProposalsViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

export function ProposalsListPage() {
  const navigate = useNavigate();
  const view = useProposalsUi((s) => s.view);
  const setView = useProposalsUi((s) => s.setView);
  const showArchived = useProposalsUi((s) => s.showArchived);
  const setShowArchived = useProposalsUi((s) => s.setShowArchived);
  const ordering = useProposalsUi((s) => s.ordering);
  const setOrdering = useProposalsUi((s) => s.setOrdering);
  // Persisted filters (survive reloads via the store).
  const status = useProposalsUi((s) => s.status);
  const setStatus = useProposalsUi((s) => s.setStatus);
  const owner = useProposalsUi((s) => s.owner);
  const setOwner = useProposalsUi((s) => s.setOwner);
  const createdFrom = useProposalsUi((s) => s.createdFrom);
  const setCreatedFrom = useProposalsUi((s) => s.setCreatedFrom);
  const createdTo = useProposalsUi((s) => s.createdTo);
  const setCreatedTo = useProposalsUi((s) => s.setCreatedTo);

  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [page, setPage] = useState(1);

  const filtersActive =
    searchInput !== '' ||
    status !== 'all' ||
    owner !== '' ||
    createdFrom !== '' ||
    createdTo !== '' ||
    showArchived;

  function resetFilters() {
    setSearchInput('');
    setStatus('all');
    setOwner('');
    setCreatedFrom('');
    setCreatedTo('');
    setShowArchived(false);
    setPage(1);
  }

  const { data, isPending, isError, error, isFetching } = useProposals({
    page,
    search: search || undefined,
    status: status === 'all' ? undefined : status,
    owner: owner ? Number(owner) : undefined,
    archived: showArchived ? true : undefined,
    created_after: createdFrom || undefined,
    created_before: endOfDayParam(createdTo),
    ordering,
  });

  const archive = useArchiveProposal();
  const restore = useRestoreProposal();
  const purge = usePurgeProposal();

  const results = data?.results ?? [];
  const onArchive = (p: Proposal) => archive.mutate(p.id);
  const onRestore = (p: Proposal) => restore.mutate(p.id);
  const onPurge = (p: Proposal) => purge.mutate(p.id);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Proposals</h1>
        <Button onClick={() => navigate('/crm/proposals/new')}>
          <Plus /> New proposal
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-56">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search proposals…"
              className="pl-8"
              aria-label="Search proposals"
            />
          </div>
          <Select
            value={status}
            onValueChange={(v) => {
              setPage(1);
              setStatus(v);
            }}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {PROPOSAL_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <MemberSelect
            value={owner}
            onChange={(v) => {
              setPage(1);
              setOwner(v);
            }}
            placeholder="All sales"
            queryKey="proposals-owner-filter"
            roles={CRM_OWNER_ROLES}
            clearable
            triggerClassName="h-9 w-auto min-w-28"
          />
          <DateRangeFilter
            label="Created"
            idPrefix="proposal-created"
            from={createdFrom}
            to={createdTo}
            onFromChange={(v) => {
              setPage(1);
              setCreatedFrom(v);
            }}
            onToChange={(v) => {
              setPage(1);
              setCreatedTo(v);
            }}
          />
          {filtersActive ? (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={resetFilters}
              aria-label="Reset all filters"
              title="Reset all filters"
            >
              <FilterX className="size-4" />
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant={showArchived ? 'default' : 'outline'}
            onClick={() => {
              setPage(1);
              setShowArchived(!showArchived);
            }}
            aria-pressed={showArchived}
            aria-label={showArchived ? 'Showing archived proposals' : 'Show archived proposals'}
            title={showArchived ? 'Showing archived proposals' : 'Show archived proposals'}
          >
            <Archive className="size-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `proposals.csv` without leaving the SPA.
              void exportCsv(PROPOSALS_BASE, {
                search: search || undefined,
                status: status === 'all' ? undefined : status,
                owner: owner ? Number(owner) : undefined,
                archived: showArchived ? true : undefined,
                created_after: createdFrom || undefined,
                created_before: endOfDayParam(createdTo),
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export proposals as CSV"
            title="Export proposals as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError).message}
        </p>
      ) : isPending ? (
        <ProposalsSkeleton />
      ) : results.length === 0 ? (
        // Empty state is consistent across views: render the table chrome with
        // an inline "No proposals found." row, so the page never collapses
        // into a tiny floating message regardless of the persisted view mode.
        <ProposalsTable
          proposals={[]}
          onRowClick={() => undefined}
          emptyMessage={showArchived ? 'No archived proposals.' : undefined}
          onArchive={onArchive}
          onRestore={onRestore}
          onPurge={onPurge}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'table' ? (
        <ProposalsTable
          proposals={results}
          onRowClick={(p) => navigate(`/crm/proposals/${p.public_id}`)}
          onArchive={onArchive}
          onRestore={onRestore}
          onPurge={onPurge}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'list' ? (
        <ProposalsList
          proposals={results}
          onOpen={(p) => navigate(`/crm/proposals/${p.public_id}`)}
        />
      ) : view === 'cards' ? (
        <ProposalsCards
          proposals={results}
          onOpen={(p) => navigate(`/crm/proposals/${p.public_id}`)}
        />
      ) : (
        <ProposalsGroupedByStatus
          proposals={results}
          onOpen={(p) => navigate(`/crm/proposals/${p.public_id}`)}
        />
      )}

      {/* Page-level USD aggregate — proposals snapshot `base_total` at creation
          so this is a real currency-correct sum across the visible page. */}
      {results.length > 0 ? (
        <p className="text-muted-foreground text-sm">
          Page total: {formatMoney(sumBase(results, 'base_total'), 'USD')}
        </p>
      ) : null}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'proposal' : 'proposals'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ListProps {
  proposals: Proposal[];
  onOpen: (p: Proposal) => void;
}

interface ProposalsTableProps {
  proposals: Proposal[];
  onRowClick: (p: Proposal) => void;
  emptyMessage?: string;
  onArchive: (p: Proposal) => void;
  onRestore: (p: Proposal) => void;
  onPurge: (p: Proposal) => void;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function ProposalsTable({
  proposals,
  onRowClick,
  emptyMessage,
  onArchive,
  onRestore,
  onPurge,
  ordering,
  onOrderingChange,
}: ProposalsTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<ProposalOrderingColumn>
              column="title"
              value={ordering}
              onChange={onOrderingChange}
            >
              Title
            </SortableTableHead>
            <SortableTableHead<ProposalOrderingColumn>
              column="status"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
            <SortableTableHead<ProposalOrderingColumn>
              column="base_total"
              value={ordering}
              onChange={onOrderingChange}
            >
              Total (USD)
            </SortableTableHead>
            <SortableTableHead<ProposalOrderingColumn>
              column="created"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden md:table-cell"
            >
              Created
            </SortableTableHead>
            <TableHead className="w-16 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {proposals.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                {emptyMessage ?? 'No proposals found.'}
              </TableCell>
            </TableRow>
          ) : (
            proposals.map((p) => (
              <TableRow
                key={p.id}
                className="cursor-pointer"
                onClick={() => onRowClick(p)}
              >
                <TableCell className="font-medium">{p.title}</TableCell>
                <TableCell>
                  <Badge variant={statusVariant(p.status)}>{statusLabel(p.status)}</Badge>
                </TableCell>
                <TableCell className="tabular-nums">
                  {p.base_total == null ? '—' : (formatMoney(p.base_total, 'USD') ?? '—')}
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {new Date(p.created).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  {p.is_archived ? (
                    <span className="inline-flex items-center justify-end">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Restore ${p.title}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRestore(p);
                        }}
                      >
                        <RotateCcw className="size-4" />
                      </Button>
                      <PurgeArchivedButton name={p.title} onPurge={() => onPurge(p)} />
                    </span>
                  ) : (
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Archive ${p.title}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onArchive(p);
                      }}
                    >
                      <Archive className="size-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function ProposalsCards({ proposals, onOpen }: ListProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {proposals.map((p) => (
        <Card
          key={p.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(p)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(p);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{p.title}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={statusVariant(p.status)}>{statusLabel(p.status)}</Badge>
            <span className="text-muted-foreground tabular-nums">
              {p.base_total == null ? '—' : (formatMoney(p.base_total, 'USD') ?? '—')}
            </span>
            <span className="text-muted-foreground ml-auto">
              {new Date(p.created).toLocaleDateString()}
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ProposalsList({ proposals, onOpen }: ListProps) {
  return (
    <ul className="divide-y rounded-md border">
      {proposals.map((p) => (
        <li key={p.id}>
          <button
            type="button"
            onClick={() => onOpen(p)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <Badge variant={statusVariant(p.status)} className="shrink-0">
              {statusLabel(p.status)}
            </Badge>
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{p.title}</span>
            <span className="text-muted-foreground hidden text-xs tabular-nums sm:inline">
              {p.base_total == null ? '—' : (formatMoney(p.base_total, 'USD') ?? '—')}
            </span>
            <span className="text-muted-foreground text-xs">
              {new Date(p.created).toLocaleDateString()}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function ProposalsGroupedByStatus({ proposals, onOpen }: ListProps) {
  // Bucket by status, preserving the PROPOSAL_STATUSES order
  // (draft → sent → accepted → rejected).
  const byStatus = useMemo(() => {
    const map = new Map<string, Proposal[]>();
    for (const status of PROPOSAL_STATUSES) map.set(status.value, []);
    for (const proposal of proposals) {
      const list = map.get(proposal.status) ?? [];
      list.push(proposal);
      map.set(proposal.status, list);
    }
    return map;
  }, [proposals]);

  return (
    <div className="space-y-6">
      {PROPOSAL_STATUSES.map(({ value, label }) => {
        const bucket = byStatus.get(value) ?? [];
        if (bucket.length === 0) return null;
        // Proposals snapshot `base_total` in USD at creation — safe to aggregate.
        const subtotal = sumBase(bucket, 'base_total');
        return (
          <section key={value} aria-labelledby={`status-${value}`}>
            <h2
              id={`status-${value}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
              {subtotal > 0 ? (
                <span className="text-foreground/80 ml-auto normal-case tracking-normal">
                  {formatMoney(subtotal, 'USD')}
                </span>
              ) : null}
            </h2>
            <ProposalsCards proposals={bucket} onOpen={onOpen} />
          </section>
        );
      })}
    </div>
  );
}

function ProposalsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
